/**
 * `/spielen` (optionally `?pin=123456` from the QR code): the student page.
 *
 * PIN and a temporary name, then the student shell (lobby, the module's
 * student view or "Schau auf die Tafel", the end).
 *
 * The seat: after joining, the server's reconnect token is kept in
 * localStorage, so a reload, a locked screen, a Wi-Fi change or a closed
 * and reopened browser tab bring the phone back without typing anything,
 * with the same name and points. The token is removed when the session
 * ends, when the student is removed, or after four hours. Nothing else is
 * stored on the device (docs/STUDENT_DEVICES.md).
 *
 * Shared tablets (U19): a reload in the same tab comes back silently. A new
 * tab or a reopened browser may be the next child, so it asks first
 * ("Bist du Mia?") instead of silently taking over the seat.
 */
import type { SessionStatePayload } from '@schulspiele/protocol';

import { createLifetime } from '@shared/lifetime';
import { esc, qs } from '@shared/dom';
import { t } from '@shared/i18n/de';
import { createStudentShell, type StudentShell } from '@shared/student-shell';
import { SessionClient, type Identity } from '@shared/ws-client';
import { loadGameClient } from '../games';
import fern from './art/fern-corner.svg?raw';
import owl from './art/owl.svg?raw';
import sapling from './art/sapling.svg?raw';
import { connectionBanner } from './layout';
import './hub.css';

const SEAT_KEY = 'schulspiele.seat';
/** In sessionStorage: the seat this tab joined with (a reload comes back without asking). */
const TAB_KEY = 'schulspiele.seatTab';
const SEAT_MAX_AGE_MS = 4 * 60 * 60 * 1000;

interface Seat {
  token: string;
  /** The PIN of the game, so a QR link to the same game reuses the seat. */
  pin: string;
  /** The child's name, for "Bist du Mia?". Missing in seats saved before U19. */
  name?: string;
  savedAt: number;
}

/** The saved seat, or null (none, expired, unreadable, storage blocked). */
export function readSeat(now = Date.now()): Seat | null {
  try {
    const raw = localStorage.getItem(SEAT_KEY);
    if (raw === null) return null;
    const seat = JSON.parse(raw) as Partial<Seat>;
    if (
      typeof seat.token !== 'string' ||
      typeof seat.savedAt !== 'number' ||
      now - seat.savedAt > SEAT_MAX_AGE_MS
    ) {
      localStorage.removeItem(SEAT_KEY);
      return null;
    }
    return {
      token: seat.token,
      pin: typeof seat.pin === 'string' ? seat.pin : '',
      ...(typeof seat.name === 'string' ? { name: seat.name } : {}),
      savedAt: seat.savedAt,
    };
  } catch {
    return null;
  }
}

function writeSeat(token: string | null, pin = '', name = '', savedAt = Date.now()): void {
  try {
    if (token === null) {
      localStorage.removeItem(SEAT_KEY);
      sessionStorage.removeItem(TAB_KEY);
    } else {
      localStorage.setItem(SEAT_KEY, JSON.stringify({ token, pin, name, savedAt } satisfies Seat));
      sessionStorage.setItem(TAB_KEY, token);
    }
    // Seats from before step 6 lived in sessionStorage.
    sessionStorage.removeItem('schulspiele.reconnectToken');
  } catch {
    // Private mode without storage: a reload needs the PIN again, joining still works.
  }
}

/** Whether this tab joined with this seat (then a reload needs no question). */
export function seatOfThisTab(token: string): boolean {
  try {
    return sessionStorage.getItem(TAB_KEY) === token;
  } catch {
    return false;
  }
}

function screen(content: string): string {
  return `<div class="screen screen--top">
    <div class="screen__content">${content}</div>
    <div class="screen__fern screen__fern--left" aria-hidden="true">${fern}</div>
  </div>`;
}

export function renderJoin(mount: HTMLElement, query: URLSearchParams): () => void {
  let client: SessionClient | null = null;
  let shell: StudentShell | null = null;
  const life = createLifetime();

  const stored = readSeat();
  // A QR link to another game starts fresh; one to the same game (or none) keeps the seat.
  const linkPin = query.get('pin') ?? '';
  if (stored !== null && (linkPin === '' || linkPin === stored.pin)) {
    if (seatOfThisTab(stored.token) || stored.name === undefined || stored.name === '')
      start({ kind: 'student-reconnect', reconnectToken: stored.token });
    else askWhoIsThis(stored, stored.name);
  } else renderForm(linkPin, '', '');

  /** Another tab or a reopened browser: maybe the next child on a shared tablet. */
  function askWhoIsThis(seat: Seat, name: string): void {
    mount.innerHTML = screen(`
      <div class="join-form join-ask" data-ask>
        <h1 style="text-align:center">${esc(t('join.askName', { name }))}</h1>
        <button class="btn btn--big" type="button" data-yes>${esc(t('join.askYes', { name }))}</button>
        <button class="btn btn--ghost" type="button" data-no>${t('join.askNo')}</button>
      </div>`);
    qs(mount, '[data-yes]').addEventListener('click', () => {
      start({ kind: 'student-reconnect', reconnectToken: seat.token });
    });
    qs(mount, '[data-no]').addEventListener('click', () => {
      // The old seat stays in the game (grey) until the teacher removes it.
      writeSeat(null);
      renderForm(seat.pin, '', '');
    });
  }

  function renderForm(pin: string, name: string, error: string): void {
    mount.innerHTML = screen(`
      <form class="join-form" data-join novalidate>
        <h1 style="text-align:center">${t('join.title')}</h1>
        <label class="field">${t('join.pin')}<input name="pin" inputmode="numeric" pattern="[1-9]{6}" maxlength="6" autocomplete="off" enterkeyhint="next" value="${esc(pin)}" /></label>
        <label class="field">${t('join.name')}<input name="name" maxlength="16" autocomplete="off" autocapitalize="words" enterkeyhint="go" value="${esc(name)}" /></label>
        <p class="join-error" data-error role="alert">${esc(error)}</p>
        <button class="btn" type="submit">${t('join.submit')}</button>
      </form>`);
    const form = qs<HTMLFormElement>(mount, '[data-join]');
    const pinInput = qs<HTMLInputElement>(form, 'input[name="pin"]');
    const nameInput = qs<HTMLInputElement>(form, 'input[name="name"]');
    (pin.length === 6 ? nameInput : pinInput).focus();
    if (error !== '') qs(form, '[data-error]').classList.add('is-shown');
    pinInput.addEventListener('input', () => {
      pinInput.value = pinInput.value.replace(/\D/g, '').slice(0, 6);
      if (pinInput.value.length === 6 && nameInput.value === '') nameInput.focus();
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const p = pinInput.value.trim();
      const n = nameInput.value.trim();
      const errorEl = qs(form, '[data-error]');
      if (!/^\d{6}$/.test(p)) {
        errorEl.textContent = t('join.pinInvalid');
        pinInput.focus();
        return;
      }
      if (n.length < 2) {
        errorEl.textContent = t('join.nameMissing');
        nameInput.focus();
        return;
      }
      start({ kind: 'student', pin: p, name: n }, { pin: p, name: n });
    });
  }

  function start(identity: Identity, typed?: { pin: string; name: string }): void {
    const seatPin = typed?.pin ?? stored?.pin ?? '';
    /** The seat's token and when it was first saved (a reconnect keeps both). */
    let seat: { token: string; savedAt: number } | null =
      identity.kind === 'student-reconnect'
        ? { token: identity.reconnectToken, savedAt: stored?.savedAt ?? Date.now() }
        : null;
    /** Keep the name with the seat, for "Bist du Mia?" on the next start. */
    let remembered = '';
    const rememberName = (name: string): void => {
      if (seat === null || name === '' || name === remembered) return;
      remembered = name;
      writeSeat(seat.token, seatPin, name, seat.savedAt);
    };
    mount.innerHTML = `<div data-banner class="shell-banner"></div><div data-stage>${screen(`<p class="lead">${t('conn.connecting')}</p>`)}</div>`;
    const stage = qs(mount, '[data-stage]');
    client = new SessionClient(identity);
    const c = client;
    let participantId = '';
    let phase: string | null = null;
    /** State that arrived before `joined` (the server sends it first); handled once we know who we are. */
    let early: SessionStatePayload | null = null;

    /** `giveUp`: the child leaves on its own; the server frees the seat. */
    const leave = (html: string, giveUp = false): void => {
      writeSeat(null);
      if (giveUp) void c.leave();
      else c.close();
      // A reconnect note would stand above the message for good.
      qs(mount, '[data-banner]').innerHTML = '';
      // Back into the same game: the PIN is filled in already.
      const again =
        giveUp || seatPin === '' ? '/spielen' : `/spielen?pin=${encodeURIComponent(seatPin)}`;
      ensureShell().showMessage(
        `${html}<a class="btn btn--big" href="${again}">${t('join.again')}</a>`,
      );
    };

    const ensureShell = (): StudentShell => {
      shell ??= createStudentShell(stage, c, { sapling, owl });
      return shell;
    };

    c.on('status', (s) => {
      if (s !== 'closed') qs(mount, '[data-banner]').innerHTML = connectionBanner(s);
    });
    c.on('joined', (j) => {
      participantId = j.participantId;
      // A reconnect keeps the PIN it was saved with (for "Neu beitreten").
      if (j.reconnectToken !== undefined) seat = { token: j.reconnectToken, savedAt: Date.now() };
      rememberName(j.name);
      // A reload should come back to this seat, not to the PIN form of the link.
      if (location.search !== '') history.replaceState(null, '', '/spielen');
      ensureShell().setName(j.name);
      // A seat in a game the teacher forgot to end: the way out, in every phase.
      ensureShell().setLeave(() => {
        leave(`<h1>${t('join.left')}</h1>`, true);
      });
      if (early !== null) {
        const s = early;
        early = null;
        void onState(s);
      }
    });
    // The server said no: wrong PIN, locked, name taken, or the seat is gone
    // (removed while the phone slept, game over, server restarted). Network
    // trouble never lands here; the client keeps reconnecting by itself.
    c.on('refused', (e) => {
      const reason = e.reason ?? t('error.generic');
      if (phase === null) {
        writeSeat(null);
        c.close();
        shell?.destroy();
        shell = null;
        // Seat removed but the game runs on: the same PIN works again.
        const samePin = reason.startsWith('Dein Platz') ? seatPin : (typed?.pin ?? '');
        renderForm(samePin, typed?.name ?? '', reason);
      } else {
        leave(`<h1>${esc(reason)}</h1>`);
      }
    });
    c.on('replaced', () => {
      ensureShell().showMessage(
        `<h1>${t('join.replaced')}</h1><a class="btn btn--big" href="/spielen">${t('join.takeBack')}</a>`,
      );
    });
    c.on('kicked', (reason) => {
      leave(`<h1>${esc(reason)}</h1>`);
    });
    c.on('state', (s) => {
      void onState(s);
    });
    c.connect();

    async function onState(s: SessionStatePayload): Promise<void> {
      if (participantId === '') {
        early = s;
        return;
      }
      const sh = ensureShell();
      if (s.name !== undefined) {
        sh.setName(s.name);
        if (s.phase !== 'ended') rememberName(s.name);
      }
      if (s.phase === 'lobby' && phase !== 'lobby') {
        phase = 'lobby';
        sh.showLobby(s.gameName, () => {
          // A seat from a game the teacher forgot to end: leave it and join another.
          leave(`<h1>${t('join.left')}</h1>`, true);
        });
      } else if (s.phase === 'running' && phase !== 'running') {
        phase = 'running';
        const game = await loadGameClient(s.gameId);
        if (life.isDisposed()) return;
        sh.showGame(game?.student, participantId);
      } else if (s.phase === 'ended' && phase !== 'ended') {
        phase = 'ended';
        writeSeat(null);
        sh.showEnded();
      }
    }
  }

  return () => {
    life.dispose();
    shell?.destroy();
    client?.close();
  };
}
